/**
 * Admin Metrics Service
 * Feature: 003-admin-dashboard-metrics
 * Updated: 007-cloudflare-migration
 *
 * Provides query functions for fetching metric counts from Cloudflare API Gateway.
 * All functions return MetricData with active and archived counts.
 */

import * as Sentry from '@sentry/react';
import { getMetrics } from './api/admin';
import { isApiError } from './api/errors';
import type { MetricData } from '../types/adminMetrics';
import type { ApiMetrics } from './api/types';

/**
 * Transform API metrics response to MetricData format
 * Uses total as active count, and sums deleted/suspended/expired as archived
 */
function transformMetrics(apiMetrics: ApiMetrics): MetricData {
  const breakdown = apiMetrics.breakdown.by_status || {};
  
  // For users: total is active, deleted is archived
  // For other types: active status count, or fall back to total
  const active = apiMetrics.total || breakdown['active'] || 0;
  const archived = (breakdown['deleted'] || 0) + (breakdown['suspended'] || 0) + (breakdown['expired'] || 0);
  
  return { active, archived };
}

/**
 * Get users metrics from API Gateway
 * Active: status = 'active'
 * Archived: status IN ('deleted', 'suspended')
 */
export async function getUsersMetrics(): Promise<MetricData> {
  try {
    const apiMetrics = await getMetrics('users');
    return transformMetrics(apiMetrics);
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'admin-metrics', metric: 'users' },
        contexts: {
          metric: {
            endpoint: '/admin/metrics/users',
            query_type: 'api',
          },
        },
      });
    }
    throw error;
  }
}

/**
 * Get businesses metrics from API Gateway
 * Active: status = 'active'
 * Archived: status = 'deleted'
 */
export async function getBusinessesMetrics(): Promise<MetricData> {
  try {
    const apiMetrics = await getMetrics('businesses');
    return transformMetrics(apiMetrics);
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'admin-metrics', metric: 'businesses' },
        contexts: {
          metric: {
            endpoint: '/admin/metrics/businesses',
            query_type: 'api',
          },
        },
      });
    }
    throw error;
  }
}

/**
 * Get jobs metrics from API Gateway
 * Active: status NOT IN ('deleted', 'expired')
 * Archived: status IN ('deleted', 'expired')
 */
export async function getJobsMetrics(): Promise<MetricData> {
  try {
    const apiMetrics = await getMetrics('jobs');
    return transformMetrics(apiMetrics);
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'admin-metrics', metric: 'jobs' },
        contexts: {
          metric: {
            endpoint: '/admin/metrics/jobs',
            query_type: 'api',
          },
        },
      });
    }
    throw error;
  }
}

/**
 * Get marketplace metrics from API Gateway
 * Active: status = 'active'
 * Archived: status IN ('deleted', 'expired')
 */
export async function getMarketplaceMetrics(): Promise<MetricData> {
  try {
    const apiMetrics = await getMetrics('marketplace');
    return transformMetrics(apiMetrics);
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'admin-metrics', metric: 'marketplace' },
        contexts: {
          metric: {
            endpoint: '/admin/metrics/marketplace',
            query_type: 'api',
          },
        },
      });
    }
    throw error;
  }
}
